/**
 * Internet Celebrity Wall popup -- video playback, drawer toggle,
 * TikTok-style vertical swipe to navigate between videos.
 *
 * Notes:
 *  - Video click toggle reads video.paused live (not a tracked flag) and is
 *    bound once at script load. Previous version re-bound inside
 *    `canplaythrough`, stacking handlers that cancelled each other.
 *  - Close logic scopes pause/hide to the popup actually clicked, not [0].
 *  - Swipe-nav only triggers when the review drawer is collapsed; reading
 *    reviews continues to scroll the drawer naturally.
 */

// Swipe gesture state, shared across the touchstart / touchend handlers below.
var celebritySwipeStartY = null;
var celebritySwipeStartX = null;
var CELEBRITY_SWIPE_THRESHOLD = 50; // px the finger must travel to count as a swipe

/**
 * Deferred hydration.
 *
 * snippets/internet-celebrity-wall_popup.liquid ships each popup's contents
 * inside a <template>; only the outer shell (data-id / data-index) is live DOM.
 * Measured on the live homepage: the 9 popups were 762 elements = 20.4% of the
 * whole document's elements, for markup nobody sees until they tap a video.
 *
 * Everything below that used to bind at script load against inner elements now
 * binds here instead, per popup, at hydration time. Deliberately NOT converted
 * to document-level delegation: the close-on-backdrop logic relies on
 * `.popupBox` calling stopPropagation() to shield clicks inside the modal, and
 * with jQuery delegation both handlers live on `document`, which changes when
 * that guard runs. Binding to the real elements keeps the semantics identical
 * to before, byte for byte.
 *
 * The popup's own touchstart/touchend (swipe-to-next) stay bound at load: they
 * sit on the shell, which never left the DOM.
 */
function celebrityPopupHydrate ($popup) {
  if (!$popup || $popup.length === 0) return $popup;
  var el = $popup[0];
  var tpl = el.querySelector(':scope > template');
  if (!tpl) return $popup; // already hydrated, or a caller that never deferred

  el.appendChild(tpl.content);
  tpl.remove();

  // --- bindings that used to run at script load, now scoped to this popup ---

  // Close on backdrop / exit-button click. Scoped to the closest popup so we
  // don't pause the wrong video.
  $popup.find(".rectangle, .rectangle .popupBox > .item .exit").on("click", function () {
    var $p = $(this).closest(".internetCelebrityWallPopup");
    $p.find("video").each(function () { this.pause(); });
    $("html").css("overflow", "");
    $p.css("display", "none");
  });

  // Stop popupBox clicks from bubbling to the backdrop close handler.
  $popup.find(".rectangle .popupBox").on("click", function (e) {
    e.stopPropagation();
  });

  // Product card links inside the popup open in a new tab. This is a
  // content-browsing modal (TikTok-style) -- following a link in the same tab
  // drops the user back at the top of the homepage when they hit Back, losing
  // their scroll position and the popup state. Opening in a new tab lets them
  // peek at the PDP and resume browsing reviews by closing the new tab.
  $popup.find('.popupProductList a[href]').each(function () {
    this.setAttribute('target', '_blank');
    this.setAttribute('rel', 'noopener noreferrer');
  });

  $popup.find(".rectangle .popupBox>.item .popupDetails .touchTheSlider").on('click', function () {
    var $slideArea = $(this);
    var type = $(this).attr('data-type');
    var nextType = type == 'open' ? 'close' : 'open';

    $slideArea.attr('data-type', nextType);
    $slideArea.find('.touchTheSlider__label').text(nextType == 'open' ? 'Tap to read review' : 'Tap to close');
    $slideArea.attr('aria-label', nextType == 'open' ? 'Tap to read review' : 'Tap to close');
  });

  // PDP 模式：当前产品的卡片被换成了这个按钮（只在 PDP 渲染）。关掉弹窗，滚到选项区。
  $popup.find("[data-celebrity-cta]").on("click", function () {
    var $p = $(this).closest(".internetCelebrityWallPopup");
    $p.find("video").each(function () { this.pause(); });
    $("html").css("overflow", "");
    $p.css("display", "none");
    celebrityLastFocus = null; // 焦点交给选项区，不再回到打开前的位置
    celebrityScrollToOptions();
  });

  // 弹窗里是整张 product-card：色块的点击/hover 换图靠 product-card.js 在
  // colorSwatches() 里绑定，卡片是刚插进来的，必须补一次，否则弹窗里的色块是死的。
  // （hover 第二张图不用管：product-card.js 里那条 pointerover 委托会在鼠标
  // 真的移上来时补 src。）
  if (typeof colorSwatches === 'function') colorSwatches();

  // GLC（Geolocation & Currency，gelacy.js）在页面加载时扫一遍 DOM，把价格换算成访客币种
  // 并打上 .glc-money / translate="no"。实测它**不监听 DOM 变化**：延迟插入的内容
  // 等 6 秒也不会被处理。美元访客看不出差别，但换汇访客会在这里看到未换算的美元价。
  // 应用自己暴露了 reConvert / reTranslate（切币种时它就调这两个），这里复用。
  // 取不到就静默跳过——应用被卸载时不能让主题跟着炸。
  try {
    if (window._gelacy) {
      if (typeof window._gelacy.reConvert === 'function') window._gelacy.reConvert();
      if (typeof window._gelacy.reTranslate === 'function') window._gelacy.reTranslate();
    }
  } catch (e) { /* 应用未安装 / 版本变更 */ }

  return $popup;
}

// 滚到 PDP 选项区：取视觉上最靠上的那行选项（.selector-wrapper 有 CSS order，
// DOM 顺序≠视觉顺序，同 utils.js scrollToEmptyOption 的取法）；找不到就退到主产品区。
function celebrityScrollToOptions () {
  var target = null;
  var best = Infinity;
  document.querySelectorAll("#MainContent #bcpo .selector-wrapper").forEach(function (el) {
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    if (rect.top < best) { best = rect.top; target = el; }
  });
  if (!target) target = document.querySelector("#MainContent .MainProduct");
  if (!target) return;
  try {
    windowScrollTop($(target), [], "html,body", 16);
  } catch (e) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  celebrityFocusOptions(target);
}

// 弹窗一藏，焦点落回 body，键盘用户下一次 Tab 会跳进别处（实测进了 Loox 评论 iframe）。
// 焦点给这一行里第一个可见、可 Tab 的控件。BCPO 的单选框大多是隐藏的（Hana/Milano
// 第一行一个可聚焦的都没有），那时退到整行本身（tabindex=-1），下一次 Tab 就从这一行往里走。
// 文本输入框不直接给：手机上会立刻弹出键盘。preventScroll —— 滚动位置上面已经算好了 header 偏移。
function celebrityFocusOptions (target) {
  var control = Array.prototype.find.call(
    target.querySelectorAll("input, select, textarea, button, a[href], [tabindex]"),
    function (el) {
      if (el.tabIndex < 0 || el.disabled || el.getClientRects().length === 0) return false;
      if (getComputedStyle(el).visibility === "hidden") return false;
      return !el.matches("textarea, select, input:not([type=radio]):not([type=checkbox]):not([type=button]):not([type=submit])");
    }
  );
  if (!control) {
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    control = target;
  }
  control.focus({ preventScroll: true });
}

// 打开弹窗前焦点所在的元素，Esc 关闭时还回去。墙上的触发器目前本身不可聚焦（既有问题），
// 所以多数情况下这里是空的；只记弹窗外、非 body 的元素，上下滑切视频时不覆盖。
var celebrityLastFocus = null;

// Esc 关闭当前打开的弹窗（键盘用户原来只能 Tab 到关闭图标——而它不可聚焦）。
document.addEventListener("keydown", function (event) {
  if (event.key !== "Escape") return;
  $(".internetCelebrityWallPopup").filter(function () {
    return this.style.display === "flex";
  }).each(function () {
    $(this).find("video").each(function () { this.pause(); });
    $("html").css("overflow", "");
    this.style.display = "none";
    // 推到本轮事件之后：Omnisend 也监听 Esc 并把焦点挪到它自己的按钮上（实测），
    // 同步还焦点会被它覆盖。
    var restore = celebrityLastFocus;
    celebrityLastFocus = null;
    if (restore && restore.isConnected) {
      setTimeout(function () { restore.focus({ preventScroll: true }); }, 0);
    }
  });
});

function internetCelebrityWallPopupOpen (sectionId, index, inheritedMuted) {
  var item = $(`.internetCelebrityWallPopup[data-id="${sectionId}"][data-index="${index}"]`);
  if (!item.length) return false;
  // 简介卡也有 popup 外壳，但没有视频；不能打断当前播放或锁住页面。
  celebrityPopupHydrate(item);
  var video = item.find("video")[0];
  if (!video || !(video.getAttribute('src') || video.querySelector('source[src]:not([src=""])'))) return false;
  // Pause any video that may have been left playing in another popup
  // (defensive -- shouldn't normally happen, but cheap insurance).
  $(".internetCelebrityWallPopup video").each(function () { this.pause(); });

  var active = document.activeElement;
  if (active && active !== document.body && !active.closest(".internetCelebrityWallPopup")) {
    celebrityLastFocus = active;
  }
  if (item.length > 0) {
    // 必须在任何 item.find(...) 之前：此刻内容可能还在 <template> 里。
    celebrityPopupHydrate(item);
    // Reset the drawer to collapsed state so each new video starts with the
    // tap-to-read affordance visible, not the previously-open review.
    item.find('.touchTheSlider').attr('data-type', 'open')
      .attr('aria-label', 'Tap to read review')
      .find('.touchTheSlider__label').text('Tap to read review');

    video.load();
    // Inherit the mute preference from the previous video, so unmuting once
    // sticks across vertical swipe navigation. Each popup has its own <video>
    // element pre-rendered with muted=true, so without this they always
    // restart silent.
    if (typeof inheritedMuted === 'boolean') {
      video.muted = inheritedMuted;
    }
    $("html").css("overflow", "hidden");
    item.css('display', 'flex');
    // slider 必须等弹窗可见后再初始化，否则 Swiper 会按 display:none 算出 0 尺寸。
    // snippets/internet-celebrity-wall_popup.liquid 会在对应主题设置下加载初始化脚本。
    if (typeof window.initProductCardSliders === 'function') {
      window.initProductCardSliders();
    }
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () { /* autoplay rejected -- user can tap to play */ });
    }
    return true;
  }
}

// NOTE: No custom click-to-toggle on the <video>. The popup uses native
// `controls`, and binding our own click handler conflicts with the native
// play/pause button: tapping the native play button fires both the native
// action (play) AND our click handler, which then reads paused=false and
// immediately pauses again -- the symptom users reported as "the play button
// doesn't work, only tapping empty space does". Letting native controls own
// play/pause is the standard YouTube/TikTok behavior and is unambiguous.

// （target=_blank 与 touchTheSlider 的绑定已移入 celebrityPopupHydrate()，见文件上方。）

// ---- Vertical swipe to jump between adjacent videos (TikTok-style). ----
// Only active while the drawer is collapsed (data-type='open'), so reading
// the review drawer scrolls naturally. Stops at first/last video.

$(".internetCelebrityWallPopup").on('touchstart', function (e) {
  var $popup = $(this);
  if ($popup.find('.touchTheSlider').attr('data-type') !== 'open') {
    celebritySwipeStartY = null;
    return;
  }
  var t = e.originalEvent.touches[0];
  celebritySwipeStartY = t.clientY;
  celebritySwipeStartX = t.clientX;
  celebritySwipeMoved = false;
});

$(".internetCelebrityWallPopup").on('touchend', function (e) {
  if (celebritySwipeStartY === null) return;
  var t = e.originalEvent.changedTouches[0];
  var dy = t.clientY - celebritySwipeStartY;
  var dx = t.clientX - celebritySwipeStartX;
  celebritySwipeStartY = null;
  celebritySwipeStartX = null;

  // Need a clear vertical swipe: passed threshold AND Y dominates X.
  if (Math.abs(dy) <= CELEBRITY_SWIPE_THRESHOLD || Math.abs(dy) <= Math.abs(dx) * 1.5) {
    return;
  }

  var $popup = $(this);
  var sectionId = $popup.attr('data-id');
  var currentIndex = parseInt($popup.attr('data-index'), 10);
  // Swipe up (dy<0) -> next video; swipe down (dy>0) -> previous video.
  var nextIndex = dy < 0 ? currentIndex + 1 : currentIndex - 1;
  var $next = $(`.internetCelebrityWallPopup[data-id="${sectionId}"][data-index="${nextIndex}"]`);
  if ($next.length === 0) return; // hit boundary; stay put.

  // Capture current video's mute state so the next video inherits it.
  var prevVideo = $popup.find('video')[0];
  var prevMuted = prevVideo ? prevVideo.muted : true;

  if (internetCelebrityWallPopupOpen(sectionId, nextIndex, prevMuted)) {
    $popup.css('display', 'none');
  }
});
